import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Brain } from 'lucide-react';
import api from '../api/client';
import EmptyState from '../components/shared/EmptyState';
import KnowledgeStats from '../components/knowledge/KnowledgeStats';
import KnowledgeChunkCard from '../components/knowledge/KnowledgeChunkCard';
import RetrievalPreview from '../components/knowledge/RetrievalPreview';
import LearningMailboxPanel from '../components/knowledge/LearningMailboxPanel';
import { useDebounce } from '../hooks/useDebounce';

const SOURCE_TYPES = ['ticket_qa', 'document'];

export default function Knowledge() {
  const { t } = useTranslation();
  const [chunks, setChunks] = useState([]);
  const [stats, setStats] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState({ shop_id: '', source_type: '', is_approved: '', search: '' });

  const debouncedSearch = useDebounce(filters.search, 300);

  const fetchChunks = useCallback(async () => {
    const params = { page };
    if (filters.shop_id) params.shop_id = filters.shop_id;
    if (filters.source_type) params.source_type = filters.source_type;
    if (filters.is_approved !== '') params.is_approved = filters.is_approved;
    if (debouncedSearch) params.search = debouncedSearch;

    try {
      const [listRes, statsRes] = await Promise.all([
        api.get('/knowledge', { params }),
        api.get('/knowledge/stats'),
      ]);
      setChunks(listRes.data.data);
      setLastPage(listRes.data.last_page);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch knowledge:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters.shop_id, filters.source_type, filters.is_approved, debouncedSearch]);

  useEffect(() => { fetchChunks(); }, [fetchChunks]);

  useEffect(() => {
    api.get('/shops').then((res) => setShops(res.data)).catch(() => setShops([]));
  }, []);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const toggleApproval = async (chunk) => {
    await api.put(`/knowledge/${chunk.id}`, { is_approved: !chunk.is_approved });
    fetchChunks();
  };

  const saveChunk = async (id, draft) => {
    await api.put(`/knowledge/${id}`, draft);
    fetchChunks();
  };

  const deleteChunk = async (id) => {
    if (!window.confirm(t('common.delete_confirm'))) return;
    await api.delete(`/knowledge/${id}`);
    fetchChunks();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('knowledge.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('knowledge.subtitle')}</p>
      </div>

      <KnowledgeStats stats={stats} />

      {shops.length > 0 && <LearningMailboxPanel shops={shops} onImported={fetchChunks} />}

      {shops.length > 0 && <RetrievalPreview shops={shops} />}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          value={filters.search}
          onChange={(e) => updateFilter('search', e.target.value)}
          placeholder={t('common.search')}
          className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        />
        <select
          value={filters.shop_id}
          onChange={(e) => updateFilter('shop_id', e.target.value)}
          className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        >
          <option value="">{t('knowledge.all_shops')}</option>
          {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={filters.source_type}
          onChange={(e) => updateFilter('source_type', e.target.value)}
          className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        >
          <option value="">{t('knowledge.all_sources')}</option>
          {SOURCE_TYPES.map((v) => <option key={v} value={v}>{t(`knowledge.source_${v}`)}</option>)}
        </select>
        <select
          value={filters.is_approved}
          onChange={(e) => updateFilter('is_approved', e.target.value)}
          className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        >
          <option value="">{t('knowledge.all_statuses')}</option>
          <option value="1">{t('knowledge.approved_only')}</option>
          <option value="0">{t('knowledge.rejected_only')}</option>
        </select>
      </div>

      {chunks.length === 0 ? (
        <EmptyState
          icon={Brain}
          title={t('knowledge.no_results')}
          description={t('knowledge.no_results_hint')}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {chunks.map((chunk) => (
              <KnowledgeChunkCard
                key={chunk.id}
                chunk={chunk}
                onToggleApproval={toggleApproval}
                onSave={saveChunk}
                onDelete={deleteChunk}
              />
            ))}
          </div>

          {lastPage > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                {t('common.back')}
              </button>
              <span className="text-sm text-gray-500">{page} / {lastPage}</span>
              <button
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page === lastPage}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                {t('knowledge.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
